import { useQueryClient } from '@tanstack/react-query';
import Link from 'next/link';
import pluralize from 'pluralize';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { type AirActionTrackingLocation, useTrackDeletedItems } from '@air/analytics';
import { Assets } from '@air/api';
import { type Asset } from '@air/api/types';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { Routes } from '~/constants/routes';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { deletedClipsSuccessAction } from '~/store/centralizedClip/actions';
import { getWorkspaceStorageKey } from '~/swr-hooks/subscriptions/useStorage';
import { useUpdateBoardStats } from '~/utils/mutateUtils/BoardStats';
import { useRemoveAssetsFromAllViews } from '~/utils/mutateUtils/GalleryAssets';

export type DeletePrivateClipsParams = {
  assetIds: Asset['id'][];
  parentBoardId?: string;
  trackLocation: AirActionTrackingLocation;
};

export const useDeletePrivateClips = () => {
  const dispatch = useDispatch();
  const { currentWorkspace } = useCurrentWorkspace();
  const { removeAssetsFromAllViews } = useRemoveAssetsFromAllViews();
  const { updateBoardStats } = useUpdateBoardStats();
  const queryClient = useQueryClient();
  const { trackDeletedItems } = useTrackDeletedItems();
  const { showToast } = useToasts();
  const deletePrivateClips = useCallback(
    async ({ assetIds, parentBoardId, trackLocation }: DeletePrivateClipsParams) => {
      try {
        if (!currentWorkspace?.id) {
          throw new Error('No current workspace id');
        }

        const response = await Assets.batchDelete({
          assetIds,
          workspaceId: currentWorkspace.id,
        });

        if (response.deletedAssetIds.length > 0) {
          showToast(
            <span>
              {pluralize('asset', response.deletedAssetIds.length, true)} moved to{' '}
              <Link href={Routes.media.recentlyDeleted} className="cursor-pointer ads:text-accent text-14 text-blue-7">
                Recently Deleted
              </Link>
            </span>,
          );
        }

        if (response.deletedAssetIds.length > 0 || response.removedAssetIds.length > 0) {
          // biome-ignore lint/suspicious/noUnnecessaryConditions: API may omit arrays; fallbacks keep merge safe if shape changes
          const mergedAssetIds = [...(response.deletedAssetIds || []), ...(response.removedAssetIds || [])];

          trackDeletedItems({
            item_ids: mergedAssetIds,
            item_total: mergedAssetIds.length,
            item_type: 'asset',
            location: trackLocation,
          });

          dispatch(deletedClipsSuccessAction({ assetIds: mergedAssetIds }));

          if (currentWorkspace) {
            void queryClient.invalidateQueries({ queryKey: getWorkspaceStorageKey(currentWorkspace?.id) });
          }

          void removeAssetsFromAllViews({ assetIds: mergedAssetIds });

          if (parentBoardId) {
            void updateBoardStats(parentBoardId);
          }
        }

        return response;
      } catch (error) {
        throw reportErrorToBugsnag({
          error,
          context: 'Failed to delete clips',
          metadata: {
            Data: {
              assetIds,
              trackLocation,
            },
          },
        });
      }
    },
    [currentWorkspace, showToast, trackDeletedItems, removeAssetsFromAllViews, dispatch, queryClient, updateBoardStats],
  );

  return { deletePrivateClips };
};

export type DeletePrivateClipsResult = Awaited<
  ReturnType<ReturnType<typeof useDeletePrivateClips>['deletePrivateClips']>
>;
